import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  QrCode, 
  Send, 
  Download, 
  CreditCard, 
  TrendingUp, 
  Plus,
  Bell,
  Eye,
  EyeOff,
  Building2
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/Button'
import { WalletCard } from '@/components/walletpage'
import { TransactionList } from '@/components/transactionspage'
import { QuickActions, KYCStatusCard, AddMoneyModal, SendMoneyModal, ReceiveMoneyModal } from '@/components/dashboardpage'
import { useWallets } from '@/hooks/useWallets'
import { formatCurrency } from '@/lib/utils'
import { useQuery } from 'react-query'
import { transactionApi, userApi, kycApi, merchantApi } from '@/services/api'
import { BusinessOnboardingModal } from '@/components/BusinessOnboardingModal'

export function DashboardPage() {
  const { t } = useTranslation()
  const [showBalance, setShowBalance] = useState(true)
  const { user, updateUser } = useAuthStore()
  const navigate = useNavigate()
  const [isAddMoneyModalOpen, setIsAddMoneyModalOpen] = useState(false)
  const [isSendMoneyModalOpen, setIsSendMoneyModalOpen] = useState(false)
  const [isReceiveMoneyModalOpen, setIsReceiveMoneyModalOpen] = useState(false)
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false)

  // Check KYC status first (before useEffect that uses it)
  const kycStatus = user?.kyc_status
  const isKYCPending = kycStatus === 'pending'
  const isKYCApproved = kycStatus === 'approved'
  const isKYCRejected = kycStatus === 'rejected'

  // Fetch and update user info from /api/users/me to ensure KYC status is up to date
  // This is especially important after KYC approval/rejection
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await userApi.getProfile()
        const userData = response.data.data || response.data
        console.log('User info from /api/users/me:', userData)
        
        if (userData) {
          const updates: Record<string, any> = {}
          if (userData.kyc_status && userData.kyc_status !== user?.kyc_status) {
            updates.kyc_status = userData.kyc_status
          }
          if (userData.user_type && userData.user_type !== user?.user_type) {
            updates.user_type = userData.user_type
          }
          if (Object.keys(updates).length > 0) {
            updateUser(updates)
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error)
      }
    }

    if (user) {
      fetchUserInfo()
      // Refetch every 30 seconds if KYC is pending to check for status updates
      if (isKYCPending) {
        const interval = setInterval(fetchUserInfo, 30000)
        return () => clearInterval(interval)
      }
    }
  }, [user, updateUser, isKYCPending])

  // Use custom hook to fetch wallets
  const { wallets, isLoading: walletsLoading, totalBalance, userCurrency, refetch } = useWallets()

  // Fetch recent transactions
  const { data: transactions, isLoading: transactionsLoading } = useQuery(
    'recent-transactions',
    async () => {
      try {
        const response = await transactionApi.getTransactions({ limit: 5 })
        
        // Handle paginated response from Django REST Framework
        // Format: { results: [...], count: ..., next: ..., previous: ... }
        // Or: { data: { results: [...] } }
        // Or: { data: [...] }
        let transactionsData = null
        const data = (response as any).data
        
        if (data) {
          // Check for paginated results (Django REST Framework format)
          if (data.results && Array.isArray(data.results)) {
            transactionsData = data.results
          }
          // Check for nested data.results
          else if (data.data?.results && Array.isArray(data.data.results)) {
            transactionsData = data.data.results
          }
          // Check for nested data.data array
          else if (data.data && Array.isArray(data.data)) {
            transactionsData = data.data
          }
          // Check if data is directly an array
          else if (Array.isArray(data)) {
            transactionsData = data
          }
        }
        
        return transactionsData || []
      } catch (error) {
        console.error('Error fetching recent transactions:', error)
        return []
      }
    },
    { refetchInterval: 10000 } // Refetch every 10 seconds
  )
  
  // Fetch KYC documents from API (only if pending, to check if documents exist)
  const { data: kycDocuments, isLoading: kycDocumentsLoading } = useQuery(
    'kyc-documents',
    async () => {
      try {
        const response = await kycApi.getDocuments()
        // Handle different response formats
        const data = (response as any).data
        if (data?.data && Array.isArray(data.data)) {
          return data.data
        } else if (data?.results && Array.isArray(data.results)) {
          return data.results
        } else if (Array.isArray(data)) {
          return data
        }
        return []
      } catch (error) {
        console.error('Error fetching KYC documents:', error)
        return []
      }
    },
    {
      enabled: !!user && isKYCPending, // Only fetch if user is logged in and KYC is pending
      refetchOnWindowFocus: false,
    }
  )
  
  // Check if user has submitted KYC documents and their status
  // Check both: 1) Documents in KYCDocument table (via API), 2) Documents in profile.verification_documents
  const { hasKYCDocuments, allDocumentsApproved } = (() => {
    // First check: KYCDocument table records (from API)
    if (kycDocuments && Array.isArray(kycDocuments) && kycDocuments.length > 0) {
      // Check if all documents are approved
      const allApproved = kycDocuments.every((doc: any) => doc.verification_status === 'approved')
      return { hasKYCDocuments: true, allDocumentsApproved: allApproved }
    }
    
    // Second check: verification_documents in profile
    const vdocs = user?.profile?.verification_documents
    if (!vdocs || typeof vdocs !== 'object') {
      return { hasKYCDocuments: false, allDocumentsApproved: false }
    }
    
    // Check if there are actual documents/videos (not just empty objects)
    const hasDocuments = vdocs.documents && 
      Array.isArray(vdocs.documents) && 
      vdocs.documents.length > 0
    
    const hasFacialVideo = vdocs.facial_video && 
      typeof vdocs.facial_video === 'object' &&
      (vdocs.facial_video.path || vdocs.facial_video.filename)
    
    const hasMovementVideo = vdocs.movement_video && 
      typeof vdocs.movement_video === 'object' &&
      (vdocs.movement_video.path || vdocs.movement_video.filename)
    
    return { 
      hasKYCDocuments: hasDocuments || hasFacialVideo || hasMovementVideo,
      allDocumentsApproved: false // Can't determine from profile data alone
    }
  })()
  
  // Only show KYC status card if:
  // 1. KYC is pending (not approved or rejected)
  // 2. Not all documents are approved (if we have document data from API)
  // This ensures that if KYC was approved but user state wasn't updated, we still hide the message
  const shouldShowKYCStatus = isKYCPending && 
    !isKYCApproved && 
    !allDocumentsApproved

  const shouldShowBusinessCard = user?.user_type === 'business'

  const { data: businessApplications } = useQuery(
    'business-applications',
    async () => {
      try {
        const response = await merchantApi.getBusinessApplications()
        const data = (response as any).data
        if (data?.data && Array.isArray(data.data)) {
          return data.data
        }
        if (data?.results && Array.isArray(data.results)) {
          return data.results
        }
        if (Array.isArray(data)) {
          return data
        }
        return []
      } catch (error) {
        console.error('Error fetching business applications:', error)
        return []
      }
    },
    {
      enabled: shouldShowBusinessCard,
      refetchOnWindowFocus: false,
    }
  )

  const latestBusinessApplication = Array.isArray(businessApplications) && businessApplications.length > 0
    ? businessApplications[0]
    : null
  const businessStatus = latestBusinessApplication?.status
  const shouldShowBusinessCTA =
    shouldShowBusinessCard && (!latestBusinessApplication || businessStatus === 'rejected')
  const shouldShowBusinessPending =
    shouldShowBusinessCard && businessStatus === 'pending'

  const handleAddMoneySuccess = () => {
    // Refetch wallets to update balance
    refetch()
  }

  const quickActions = [
    {
      id: 'qr-scanner',
      label: t('navigation.qrScanner'),
      icon: QrCode,
      color: 'bg-primary',
      onClick: () => navigate('/qr-scanner')
    },
    {
      id: 'send',
      label: t('wallet.sendMoney'),
      icon: Send,
      color: 'bg-secondary',
      onClick: () => setIsSendMoneyModalOpen(true)
    },
    {
      id: 'receive',
      label: t('wallet.receiveMoney'),
      icon: Download,
      color: 'bg-accent',
      onClick: () => setIsReceiveMoneyModalOpen(true)
    },
    {
      id: 'add-money',
      label: t('wallet.addMoney'),
      icon: Plus,
      color: 'bg-success',
      onClick: () => setIsAddMoneyModalOpen(true)
    }
  ]

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">
              {t('dashboard.welcome')}, {(() => {
                const firstName = user?.profile?.first_name?.trim()
                  || user?.first_name?.trim()
                const lastName = user?.profile?.last_name?.trim()
                  || user?.last_name?.trim()

                if (firstName && lastName) {
                  return `${firstName} ${lastName}`
                }
                if (firstName) {
                  return firstName
                }
                if (lastName) {
                  return lastName
                }
                return t('dashboard.guest', 'Convidado')
              })()}!
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            
            <button 
              onClick={() => setShowBalance(!showBalance)}
              className="p-2 rounded-full bg-white/20"
            >
              {showBalance ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-sm rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/80 text-sm">{t('dashboard.totalBalance')}</span>
            <span className="text-white/60 text-xs">STP</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold">
              {showBalance ? formatCurrency(totalBalance, 'STN') : '••••••'}
            </span>
          </div>
        </motion.div>
      </div>

      {/* KYC Status Card - Show when KYC is pending and not approved */}
      {shouldShowKYCStatus && (
        <div className="px-6">
          <KYCStatusCard hasDocuments={!!hasKYCDocuments} />
        </div>
      )}

      {(shouldShowBusinessCTA || shouldShowBusinessPending) && (
        <div className="px-6 mt-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-start gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text-primary">
                {t('dashboard.businessTitle', 'Registo empresarial')}
              </h3>
              <p className="text-sm text-text-secondary">
                {shouldShowBusinessPending
                  ? t(
                      'dashboard.businessPendingSubtitle',
                      'O seu registo empresarial está em análise. Resposta em até 1 dia útil.'
                    )
                  : t(
                      'dashboard.businessSubtitle',
                      'Completa o registo da tua empresa para aparecer no mapa da Djelo.'
                    )}
              </p>
            </div>
            {shouldShowBusinessCTA && (
              <Button onClick={() => setIsBusinessModalOpen(true)}>
                {t('dashboard.businessAction', 'Completar registo')}
              </Button>
            )}
          </motion.div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            {t('dashboard.quickActions')}
          </h2>
          <QuickActions actions={quickActions} />
        </motion.div>
      </div>

      {/* Wallets */}
      {wallets && wallets.length > 0 && (
        <div className="px-6 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              {t('wallet.title')}
            </h2>
            <div className="space-y-3">
                  {wallets.map((wallet: any, index: number) => (
                    <WalletCard 
                      key={wallet.id} 
                      wallet={wallet} 
                      showBalance={showBalance}
                      delay={index * 0.1}
                    />
                  ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="px-6 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary">
              {t('dashboard.recentTransactions')}
            </h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/transactions')}
            >
              {t('dashboard.viewAll')}
            </Button>
          </div>
          
          {transactionsLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
                </div>
              ))}
            </div>
          ) : transactions && transactions.length > 0 ? (
            <TransactionList 
              transactions={transactions.slice(0, 5)} 
              showBalance={showBalance}
            />
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-text-secondary">{t('dashboard.noTransactions')}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3"
                onClick={() => navigate('/qr-scanner')}
              >
                {t('transactions.firstTransaction')}
              </Button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Add Money Modal */}
      <AddMoneyModal
        isOpen={isAddMoneyModalOpen}
        onClose={() => setIsAddMoneyModalOpen(false)}
        userCurrency={userCurrency}
        wallets={wallets}
        onSuccess={handleAddMoneySuccess}
      />

      {/* Send Money Modal */}
      <SendMoneyModal
        isOpen={isSendMoneyModalOpen}
        onClose={() => setIsSendMoneyModalOpen(false)}
        onSuccess={handleAddMoneySuccess}
      />

      {/* Receive Money Modal */}
      <ReceiveMoneyModal
        isOpen={isReceiveMoneyModalOpen}
        onClose={() => setIsReceiveMoneyModalOpen(false)}
        onSuccess={handleAddMoneySuccess}
      />

      <BusinessOnboardingModal
        isOpen={isBusinessModalOpen}
        onClose={() => setIsBusinessModalOpen(false)}
        onComplete={() => setIsBusinessModalOpen(false)}
      />
    </div>
  )
}
